// Seed data mirroring engine/migrations/0008_seed_dev.up.sql (+ 0009/0010).
// Same UUIDs, same business data — so the demo UI shows the same picture
// the dispatcher would see after applying the migrations.
//
// Each entity is exported so feature stores can pick what they need.

import type {
  Client,
  ClientAddress,
  Notification,
  User,
  Vehicle,
  Warehouse,
} from "./schemaTypes";

const ACCOUNT_ID = "00000000-0000-0000-0000-000000000001";

// ---------------- Users ----------------
export const SEED_USERS: User[] = [
  {
    id: "00000000-0000-0000-0000-000000000010",
    account_id: ACCOUNT_ID,
    role: "admin",
    email: "admin@acme.local",
    full_name: "Admin Acme",
    phone: "+51900000001",
    active: true,
  },
  {
    id: "00000000-0000-0000-0000-000000000011",
    account_id: ACCOUNT_ID,
    role: "coordinator",
    email: "coord@acme.local",
    full_name: "Carla Coordinadora",
    phone: "+51900000002",
    active: true,
  },
  {
    id: "00000000-0000-0000-0000-000000000012",
    account_id: ACCOUNT_ID,
    role: "supervisor",
    email: "super@acme.local",
    full_name: "Sergio Supervisor",
    phone: "+51900000003",
    active: true,
  },
  {
    id: "00000000-0000-0000-0000-000000000020",
    account_id: ACCOUNT_ID,
    role: "driver",
    email: "carlos@acme.local",
    full_name: "Carlos Mendoza",
    phone: "+51900000010",
    active: true,
  },
  {
    id: "00000000-0000-0000-0000-000000000021",
    account_id: ACCOUNT_ID,
    role: "driver",
    email: "maria@acme.local",
    full_name: "María Rojas",
    phone: "+51900000011",
    active: true,
  },
  {
    id: "00000000-0000-0000-0000-000000000022",
    account_id: ACCOUNT_ID,
    role: "driver",
    email: "luis@acme.local",
    full_name: "Luis Quispe",
    phone: "+51900000012",
    active: true,
  },
];

// ---------------- Warehouses ----------------
export const SEED_WAREHOUSES: Warehouse[] = [
  {
    id: "00000000-0000-0000-0000-000000000100",
    account_id: ACCOUNT_ID,
    code: "LIMA-CENTRO",
    name: "Almacén Lima Centro",
    address: "Plaza Mayor, Cercado de Lima",
    lat: -12.0464,
    lon: -77.0428,
    active: true,
  },
];

// ---------------- Vehicles ----------------
export const SEED_VEHICLES: Vehicle[] = [
  {
    id: "00000000-0000-0000-0000-000000000300",
    account_id: ACCOUNT_ID,
    warehouse_id: "00000000-0000-0000-0000-000000000100",
    vehicle_type_id: "00000000-0000-0000-0000-000000000200",
    plate: "ABC-101",
    current_driver_user_id: "00000000-0000-0000-0000-000000000020",
    active: true,
  },
  {
    id: "00000000-0000-0000-0000-000000000301",
    account_id: ACCOUNT_ID,
    warehouse_id: "00000000-0000-0000-0000-000000000100",
    vehicle_type_id: "00000000-0000-0000-0000-000000000201",
    plate: "DEF-201",
    current_driver_user_id: "00000000-0000-0000-0000-000000000021",
    active: true,
  },
  {
    id: "00000000-0000-0000-0000-000000000302",
    account_id: ACCOUNT_ID,
    warehouse_id: "00000000-0000-0000-0000-000000000100",
    vehicle_type_id: "00000000-0000-0000-0000-000000000202",
    plate: "GHI-301",
    current_driver_user_id: "00000000-0000-0000-0000-000000000022",
    active: true,
  },
];

// ---------------- Clients + addresses ----------------
const C = (
  partial: Omit<Client, "account_id" | "city" | "active">,
): Client => ({
  ...partial,
  account_id: ACCOUNT_ID,
  city: "Lima",
  active: true,
});

export const SEED_CLIENTS: Client[] = [
  C({
    id: "c-001",
    code: "CLI-00001",
    name: "Bodega La Esquina",
    tax_id_type: "RUC",
    tax_id: "20512345671",
    contact_name: "José Pérez",
    contact_phone: "+51987100001",
    address: "Av. Abancay 532",
    district: "Cercado de Lima",
    lat: -12.051,
    lon: -77.032,
    business_type: "bodega",
  }),
  C({
    id: "c-002",
    code: "CLI-00002",
    name: "Minimarket San Isidro",
    tax_id_type: "RUC",
    tax_id: "20512345672",
    contact_name: "Lucía Vela",
    contact_phone: "+51987100002",
    address: "Av. Petit Thouars 4500",
    district: "San Isidro",
    lat: -12.095,
    lon: -77.026,
    business_type: "minimarket",
  }),
  C({
    id: "c-003",
    code: "CLI-00003",
    name: "Panadería El Sol",
    tax_id_type: "RUC",
    tax_id: "20512345673",
    contact_name: "Mario Gómez",
    contact_phone: "+51987100003",
    address: "Av. Brasil 2200",
    district: "Pueblo Libre",
    lat: -12.075,
    lon: -77.067,
    business_type: "panaderia",
  }),
  C({
    id: "c-004",
    code: "CLI-00004",
    name: "Restaurante Don Pepe",
    tax_id_type: "RUC",
    tax_id: "20512345674",
    contact_name: "Pedro Salas",
    contact_phone: "+51987100004",
    address: "Av. Larco 800",
    district: "Miraflores",
    lat: -12.123,
    lon: -77.029,
    business_type: "restaurante",
  }),
  C({
    id: "c-005",
    code: "CLI-00005",
    name: "Bodega Los Olivos",
    tax_id_type: "DNI",
    tax_id: "47123456",
    contact_name: "Rosa Aguilar",
    contact_phone: "+51987100005",
    address: "Av. Antúnez de Mayolo 1500",
    district: "Los Olivos",
    lat: -11.989,
    lon: -77.071,
    business_type: "bodega",
  }),
  C({
    id: "c-006",
    code: "CLI-00006",
    name: "Minimarket Surco",
    tax_id_type: "RUC",
    tax_id: "20512345676",
    contact_name: "Ana Soto",
    contact_phone: "+51987100006",
    address: "Av. Caminos del Inca 1800",
    district: "Surco",
    lat: -12.141,
    lon: -76.992,
    business_type: "minimarket",
  }),
  C({
    id: "c-007",
    code: "CLI-00007",
    name: "Cafetería Café+Café",
    tax_id_type: "RUC",
    tax_id: "20512345677",
    contact_name: "Diego Torres",
    contact_phone: "+51987100007",
    address: "Av. Conquistadores 700",
    district: "San Isidro",
    lat: -12.0985,
    lon: -77.0345,
    business_type: "cafeteria",
  }),
  C({
    id: "c-008",
    code: "CLI-00008",
    name: "Distribuidora Plaza",
    tax_id_type: "RUC",
    tax_id: "20512345678",
    contact_name: "Lucía Paredes",
    contact_phone: "+51987100008",
    address: "Av. Argentina 3200",
    district: "Callao",
    lat: -12.044,
    lon: -77.118,
    business_type: "distribuidora",
  }),
  C({
    id: "c-009",
    code: "CLI-00009",
    name: "Restaurante La Cevichería",
    tax_id_type: "RUC",
    tax_id: "20512345679",
    contact_name: "Marco Nuñez",
    contact_phone: "+51987100009",
    address: "Av. La Mar 1500",
    district: "Miraflores",
    lat: -12.114,
    lon: -77.048,
    business_type: "restaurante",
  }),
  C({
    id: "c-010",
    code: "CLI-00010",
    name: "Bodega Don Juan",
    tax_id_type: "DNI",
    tax_id: "47123457",
    contact_name: "Juan Castro",
    contact_phone: "+51987100010",
    address: "Av. Universitaria 4800",
    district: "Los Olivos",
    lat: -11.992,
    lon: -77.082,
    business_type: "bodega",
  }),
];

// Primary addresses + a couple extra to demo the 1-N relationship.
export const SEED_CLIENT_ADDRESSES: ClientAddress[] = [
  ...SEED_CLIENTS.map<ClientAddress>((c) => ({
    id: `addr-${c.id}-1`,
    client_id: c.id,
    label: "Sede principal",
    address: c.address ?? "",
    district: c.district,
    city: c.city,
    lat: c.lat,
    lon: c.lon,
    contact_name: c.contact_name,
    contact_phone: c.contact_phone,
    is_primary: true,
    active: true,
  })),
  // Distribuidora Plaza tiene 2 sucursales extra
  {
    id: "addr-c-008-2",
    client_id: "c-008",
    label: "Sucursal Surco",
    address: "Av. Caminos del Inca 2400",
    district: "Surco",
    city: "Lima",
    lat: -12.145,
    lon: -76.99,
    contact_name: "Lucía Paredes",
    contact_phone: "+51987100008",
    is_primary: false,
    active: true,
  },
  {
    id: "addr-c-008-3",
    client_id: "c-008",
    label: "Sucursal San Miguel",
    address: "Av. La Marina 2100",
    district: "San Miguel",
    city: "Lima",
    lat: -12.082,
    lon: -77.094,
    contact_name: "Lucía Paredes",
    contact_phone: "+51987100008",
    is_primary: false,
    active: true,
  },
];

// ---------------- Notifications ----------------
export const SEED_NOTIFICATIONS: Notification[] = [
  {
    id: "n-001",
    account_id: ACCOUNT_ID,
    target_user_id: "00000000-0000-0000-0000-000000000012",
    type: "kpi.rejection_rate",
    severity: "warning",
    title: "Tasa de rechazo elevada · Carlos Mendoza",
    body: "El chofer superó el 12% de rechazos en la jornada de hoy.",
    related_driver_id: "00000000-0000-0000-0000-000000000020",
    created_at: new Date(Date.now() - 1000 * 60 * 25).toISOString(),
  },
  {
    id: "n-002",
    account_id: ACCOUNT_ID,
    target_user_id: "00000000-0000-0000-0000-000000000012",
    type: "driver.offline",
    severity: "critical",
    title: "GPS sin señal · María Rojas",
    body: "Sin pings de GPS por más de 30 min.",
    related_driver_id: "00000000-0000-0000-0000-000000000021",
    created_at: new Date(Date.now() - 1000 * 60 * 12).toISOString(),
  },
  {
    id: "n-003",
    account_id: ACCOUNT_ID,
    target_user_id: "00000000-0000-0000-0000-000000000012",
    type: "route.completed",
    severity: "info",
    title: "Ruta finalizada · GHI-301",
    body: "Luis Quispe completó su jornada con 100% de cumplimiento.",
    related_driver_id: "00000000-0000-0000-0000-000000000022",
    read_at: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
    created_at: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
  },
  {
    id: "n-004",
    account_id: ACCOUNT_ID,
    target_user_id: "00000000-0000-0000-0000-000000000012",
    type: "order.cancelled",
    severity: "info",
    title: "Orden cancelada · ORD-001-0042",
    body: "El despachador canceló una orden pendiente.",
    related_order_id: "ord-0042",
    created_at: new Date(Date.now() - 1000 * 60 * 90).toISOString(),
  },
  {
    id: "n-005",
    account_id: ACCOUNT_ID,
    target_user_id: "00000000-0000-0000-0000-000000000012",
    type: "kpi.compliance",
    severity: "warning",
    title: "Cumplimiento bajo 85% · Almacén Lima Centro",
    body: "La meta diaria está en riesgo. Revisar rutas en curso.",
    created_at: new Date(Date.now() - 1000 * 60 * 180).toISOString(),
  },
];
